import React from 'react';
import { Sparkles, SlidersHorizontal, Coins, Loader2, Play } from 'lucide-react';
import { ProjectHeader } from '@/components/video-editor/ProjectHeader';

// Sticky top bar for the Video Enhancer: ProjectHeader (project selector,
// import/upload, templates) plus the enhancer controls row — mode toggle,
// aspect ratio, credits chip, and the Render button.

export type EnhancerMode = 'lambda' | 'direct'; // lambda = AI Create, direct = Timeline

interface EnhancerTopBarProps {
  projectHeader: React.ComponentProps<typeof ProjectHeader>;
  renderMode: EnhancerMode;
  onModeChange: (mode: EnhancerMode) => void;
  aspectRatio: string;
  onAspectRatioChange: (ratio: string) => void;
  creditsLabel: string | null;
  isRendering: boolean;
  onRender: () => void;
}

const ASPECT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'source', label: 'Match source' },
  { value: '16:9', label: '16:9 · Landscape' },
  { value: '9:16', label: '9:16 · Portrait' },
  { value: '1:1', label: '1:1 · Square' },
  { value: '4:5', label: '4:5 · Instagram' },
];

export const EnhancerTopBar: React.FC<EnhancerTopBarProps> = ({
  projectHeader,
  renderMode,
  onModeChange,
  aspectRatio,
  onAspectRatioChange,
  creditsLabel,
  isRendering,
  onRender,
}) => {
  const modePill = (mode: EnhancerMode, label: string, icon: React.ReactNode) => {
    const active = renderMode === mode;
    return (
      <button
        type="button"
        onClick={() => onModeChange(mode)}
        className="inline-flex items-center gap-1.5"
        style={{
          padding: '6px 14px',
          borderRadius: 999,
          border: `1px solid ${active ? 'var(--hj-blue)' : 'transparent'}`,
          background: active ? 'var(--hj-blue)' : 'transparent',
          color: active ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-2)',
          fontSize: 12.5,
          fontWeight: active ? 700 : 500,
          fontFamily: 'inherit',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {icon}
        {label}
      </button>
    );
  };

  return (
    <div
      className="sticky top-0"
      style={{
        zIndex: 40,
        background: 'var(--hj-surface-1)',
        borderBottom: '1px solid var(--hj-hairline)',
      }}
    >
      <ProjectHeader {...projectHeader} />

      <div
        className="flex flex-wrap items-center gap-2"
        style={{ padding: '8px 16px', borderTop: '1px solid var(--hj-hairline-soft)' }}
      >
        {/* Mode toggle */}
        <div
          className="inline-flex items-center"
          style={{
            padding: 3,
            borderRadius: 999,
            border: '1px solid var(--hj-hairline)',
            background: 'var(--hj-surface-2)',
            gap: 2,
          }}
        >
          {modePill('lambda', 'AI Create', <Sparkles className="w-3.5 h-3.5" />)}
          {modePill('direct', 'Timeline', <SlidersHorizontal className="w-3.5 h-3.5" />)}
        </div>

        <span className="hidden md:inline" style={{ fontSize: 12, color: 'var(--hj-ink-3)' }}>
          {renderMode === 'lambda'
            ? 'AI reads your transcripts and makes the edit decisions.'
            : 'You control cuts, titles, transitions, and effects.'}
        </span>

        <div className="flex items-center gap-2" style={{ marginLeft: 'auto' }}>
          {/* Aspect ratio */}
          <select
            value={aspectRatio}
            onChange={(e) => onAspectRatioChange(e.target.value)}
            aria-label="Output aspect ratio"
            style={{
              padding: '6px 10px',
              borderRadius: 999,
              border: '1px solid var(--hj-hairline)',
              background: 'var(--hj-surface-2)',
              color: 'var(--hj-ink)',
              fontSize: 12,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            {ASPECT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Credits chip */}
          {creditsLabel && (
            <span
              className="inline-flex items-center gap-1.5"
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border: '1px solid var(--hj-hairline)',
                background: 'var(--hj-surface-2)',
                color: 'var(--hj-ink)',
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              <Coins className="w-3.5 h-3.5" style={{ color: 'var(--hj-yellow-ink)' }} />
              {creditsLabel}
            </span>
          )}

          {/* Render */}
          <button
            type="button"
            onClick={onRender}
            disabled={isRendering}
            className="inline-flex items-center gap-1.5"
            style={{
              padding: '8px 18px',
              borderRadius: 999,
              background: 'var(--hj-blue)',
              border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              fontSize: 13,
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: isRendering ? 'default' : 'pointer',
              opacity: isRendering ? 0.7 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {isRendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" style={{ fill: 'currentColor' }} />}
            Render
          </button>
        </div>
      </div>
    </div>
  );
};
